import React, { useState } from 'react';
import SEO from './SEO';
import LessonHero from './LessonHero';
import './LessonShell.css';

/**
 * Presentation shell for a single-subject lesson page.
 *
 * Shared by every lesson type -- topic lessons and grammar lessons both supply
 * the same frame: hero header, activity navigation strip, one content panel at
 * a time, and the presentation controls. The shell knows nothing about where
 * the panels come from; an adapter (TopicsPageLayout, GrammarPageLayout)
 * converts its own data into `panels`.
 *
 * panels: [{ label, title, content }] -- label is the short name on the step
 * strip and in the panel eyebrow, title is the heading inside the panel. Both
 * are optional; the panel falls back to the label.
 */
const LessonShell = ({
  title,
  image,
  level,
  eyebrow = 'Lesson',
  description,
  seoTitle,
  seoDescription,
  seoName,
  panels,
  children,
}) => {
  const total = panels.length;
  const [activeIndex, setActiveIndex] = useState(0);

  const current = Math.min(activeIndex, Math.max(total - 1, 0));
  const hasPrev = current > 0;
  const hasNext = current < total - 1;

  const goPrev = () => setActiveIndex((i) => Math.max(i - 1, 0));
  const goNext = () => setActiveIndex((i) => Math.min(i + 1, total - 1));

  const currentPanel = total ? panels[current] : null;
  const currentLabel = currentPanel?.label || '';
  const currentTitle = currentPanel?.title || currentLabel;
  const nextTitle = hasNext ? panels[current + 1].title || panels[current + 1].label : '';

  return (
    <div className="lesson-page">
      <SEO
        title={seoTitle || 'Free English Lesson Plans and Materials'}
        description={seoDescription || 'Free English practice materials, exercises and lesson plans for teachers and students.'}
        name={seoName}
        type="article"
        image={image}
      />

      {/* ---------- LESSON HEADER ---------- */}
      <LessonHero
        title={title}
        image={image}
        level={level}
        eyebrow={eyebrow}
        description={
          description ||
          'Work through the activities below — each one builds on the last.'
        }
      />

      <main className="lesson-main">
        {/* ---------- ACTIVITY NAVIGATION STRIP ---------- */}
        {total > 0 && (
          <nav className="lesson-steps" aria-label="Lesson activities">
            <ol className="lesson-steps__list">
              {panels.map((panel, i) => (
                <li
                  key={i}
                  className={`lesson-steps__item${i === current ? ' is-active' : ''}${
                    i < current ? ' is-done' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="lesson-steps__btn"
                    onClick={() => setActiveIndex(i)}
                    aria-current={i === current ? 'step' : undefined}
                  >
                    <span className="lesson-steps__num">{i + 1}</span>
                    <span className="lesson-steps__label">{panel.label || 'Activity'}</span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* ---------- CURRENT ACTIVITY PANEL ---------- */}
        {currentPanel && (
          <section className="lesson-panel" aria-live="polite">
            <header className="lesson-panel__head">
              <p className="lesson-panel__eyebrow">{currentLabel}</p>
              {currentTitle && currentTitle !== currentLabel && (
                <h2 className="lesson-panel__title">{currentTitle}</h2>
              )}
            </header>

            <div className="lesson-panel__body">{currentPanel.content}</div>
          </section>
        )}

        {/* ---------- PRESENTATION CONTROLS ---------- */}
        {total > 1 && (
          <div className="lesson-controls">
            <button
              type="button"
              className="lesson-controls__btn"
              onClick={goPrev}
              disabled={!hasPrev}
            >
              <span aria-hidden="true">←</span> Previous
            </button>

            <span className="lesson-controls__count">
              {current + 1} / {total}
            </span>

            <button
              type="button"
              className="lesson-controls__btn lesson-controls__btn--next"
              onClick={goNext}
              disabled={!hasNext}
            >
              {hasNext ? `Next: ${nextTitle}` : 'Next'}
              <span aria-hidden="true"> →</span>
            </button>
          </div>
        )}

        {/* ---------- PAGE FOOTER CONTENT ----------
            Anything a page wants under the controls: related exercises,
            further reading, and so on. */}
        {children}
      </main>
    </div>
  );
};

export default LessonShell;